import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Clock, Truck } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import Layout from "./Layout";
import SEOHead from "./SEOHead";
import Breadcrumb from "./Breadcrumb";
import FAQSection from "./FAQSection";
import { allServices } from "../data/allServices";
import { business } from "../config/business";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const process = [
  { title: "Design", text: "Structural design engineered to your span, height and load requirements." },
  { title: "Fabrication", text: "Steel cut, drilled and coated at our Hyderabad facility." },
  { title: "Delivery", text: "Transport coordinated to your site across Telangana or AP." },
  { title: "Erection", text: "On-site assembly through to a handover-ready structure." },
];

const faqs = [
  { q: "Which service do I need for my project?", a: "It depends on your building type and scope — most projects combine PEB design, fabrication and erection into one coordinated service. Share your requirements and we'll recommend the right approach." },
  { q: "Can you handle a project end-to-end, or just one stage?", a: "We typically manage design, fabrication and erection as one project, but can also scope individual stages (e.g. erection only) depending on what you need." },
];

const Products = () => {
  return (
    <Layout>
      <SEOHead
        title="Products & Services | PEB, Industrial Sheds & Steel Fabrication — TR Structures"
        description="Explore TR Structures' full range of PEB, industrial shed, steel fabrication and prefabricated building services in Hyderabad, delivered across Telangana and Andhra Pradesh."
        keywords="TR Structures products, PEB services Hyderabad, industrial shed services, steel fabrication services"
      />
      <Breadcrumb items={[{ name: "Products" }]} />

      {/* 1. Hero */}
      <section className="section-padding pb-10">
        <div className="container-custom">
          <h1 className="font-montserrat text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 max-w-3xl">
            Our <span className="text-secondary">Products & Services</span>
          </h1>
          <p className="text-muted-foreground max-w-3xl text-lg mb-8">
            From design through erection, we build PEB structures, industrial sheds and steel
            fabrication solutions engineered around your specific requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/enquiry" className="btn-primary inline-block px-8 py-4 text-center">
              Get Project Estimate
            </Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
              <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* 2. Trust strip */}
      <section className="bg-primary py-6">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
            <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Design to erection, one team</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> Fast, parallel-track construction</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Truck size={18} aria-hidden="true" /> Telangana & AP delivery</span>
          </div>
        </div>
      </section>

      {/* 3. Services grid */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            transition={{ staggerChildren: 0.08 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {allServices.map((service) => (
              <motion.div key={service.path} variants={fadeUp}>
                <Link
                  to={service.path}
                  className="group p-6 bg-card rounded-xl shadow-md card-hover border border-border flex flex-col h-full"
                >
                  <h2 className="font-montserrat font-bold text-lg text-foreground mb-2">{service.name}</h2>
                  <p className="text-sm text-muted-foreground mb-4 flex-grow">{service.tagline}</p>
                  <span className="inline-flex items-center gap-1 text-primary font-semibold text-sm group-hover:gap-2 transition-all">
                    Learn More <ArrowRight size={16} aria-hidden="true" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 4. Process */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
            How We Work
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((step, i) => (
              <div key={step.title} className="p-6 bg-card rounded-xl shadow-md text-center card-hover">
                <p className="text-xs font-montserrat font-bold text-secondary mb-2">STEP {i + 1}</p>
                <h3 className="font-montserrat font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Industries cross-link */}
      <section className="section-padding">
        <div className="container-custom text-center">
          <h2 className="font-montserrat text-2xl font-bold text-foreground mb-4">
            Looking for Industry-Specific Requirements?
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            See how our services apply to your specific industry's structural needs.
          </p>
          <Link to="/industries" className="btn-outline inline-block px-8 py-3">
            Explore Industries We Serve
          </Link>
        </div>
      </section>

      {/* 6. FAQ */}
      <FAQSection faqs={faqs} />

      {/* 7. CTA */}
      <section className="section-padding bg-secondary">
        <div className="container-custom text-center">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
            Ready to Discuss Your Project?
          </h2>
          <p className="text-secondary-foreground/90 mb-8 max-w-xl mx-auto">
            Tell us what you're building and we'll recommend the right service and approach.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/enquiry" className="btn-primary inline-block px-8 py-4">
              Get Project Estimate
            </Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
              <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Products;
